import axios from 'axios';
import filter from 'lodash/filter';
import groupBy from 'lodash/groupBy';
import map from 'lodash/map';
import pick from 'lodash/pick';
import reverse from 'lodash/reverse';
import sortBy from 'lodash/sortBy';
import uniqBy from 'lodash/uniqBy';
import values from 'lodash/values';

export interface Metadata {
	DocumentDate: string;
	description?: string;
	docId: string;
	docName?: string;
	docType?: string;
	effectiveDate?: string;
	endorsementType?: string;
	formId?: string;
	policyNum: string;
	riskName?: string;
	year?: string;
}

/**
 * A simple constructor to turn ordinary objects into
 * `PolicyDocumentMetadata` instances, which is useful in prop validation.
 */
export class PolicyDocumentMetadata {
	constructor(metadata: unknown) {
		Object.assign(this, metadata);
	}
}

/**
 * A collection of documents all pertaining to the same policy.
 */
export class PolicyPacket {
	public term: any;
	public policyNumber: any;
	public metadatas: any;
	public effectiveFrom: any;
	public effectiveTo: any;

	constructor(term, policyNumber, metadatas) {
		this.term = term;
		this.policyNumber = policyNumber;
		this.metadatas = metadatas;
	}
}

const PolicyDocuments = {
	/**
	 * Gets all the metadatas for documents related to the given @param policyNumber.
	 *
	 * @param {string} policyNumber
	 * @returns {Promise<Array<PolicyDocumentMetadata>>}
	 */
	async getPolicyDocumentMetadatas(policyNumber) {
		const response = await axios({
			method: 'GET',
			url: `${process.env.VUE_APP_API_URI2}/documents/?policy_num=${policyNumber}`,
		});

		return response.data;
	},

	/**
	 * Gets a signed URL for the document with the given `policyNumber` and `id`.
	 *
	 * @param {string} id
	 * @returns {Promise<string>}
	 */
	async getPolicyDocumentLocation(policyNumber, id) {
		const response = await axios({
			method: 'GET',
			url: `${process.env.VUE_APP_POLICY_DOCS_URI}/policy-documents/${policyNumber}/${id}/location`,
		});

		const location = response.headers.location;
		if (!location) {
			throw new Error("Header 'location' header was not present in the response");
		}

		return location;
	},
	/**
	 * Request Policy document with the given `policyNumber` and `id` and `year`.
	 *
	 * @param {string} id
	 * @returns {Promise<string>}
	 */
	async requestPolicyDocument(
		policyNumber,
		id,
		year,
	): Promise<{ fetching: boolean; s3url?: string }> {
		const response = await axios({
			method: 'GET',
			url:
				`${process.env.VUE_APP_API_URI2}/documents/` +
				'?policy_num=' +
				policyNumber +
				'&year=' +
				year +
				'&document_id=' +
				id +
				'&cmd=get',
		});

		return response.data;
	},

	async requestInvoice(policyNumber, id, year) {
		const response = await axios({
			method: 'GET',
			url:
				`${process.env.VUE_APP_API_URI2}/documents/` +
				'?policy_num=' +
				policyNumber +
				'&year=' +
				year +
				'&document_id=' +
				id +
				'&cmd=get&getInvoice=true',
		});

		return response.data;
	},

	/**
	 * Request Policy document with the given `policyNumber` and `id` and `year`.
	 *
	 * @param {string} id
	 * @returns {Promise<string>}
	 */
	async fetchPolicyDocument(s3Url) {
		// @ts-ignore
		const response = await axios({
			method: 'GET',
			url: s3Url,
			headers: {
				'Content-Type': 'application/pdf',
			},
			skipAuth: true,
		});

		return response.data;
	},

	/**
	 * Groups a collection of metadatas into an array {@link PolicyPacket}s by their term year and policy number.
	 *
	 * @param {Array<PolicyDocumentMetadata>} metadatasArray
	 */
	packetizePolicyDocumentMetadatas(metadatasArray) {
		// Deduplicate to accept only the newest revision of each document.
		const criteria = ['term', 'policyNumber', 'formId', 'riskName', 'timestamp'];
		metadatasArray = sortBy(metadatasArray, criteria);
		metadatasArray = reverse(metadatasArray);

		metadatasArray = uniqBy(metadatasArray, metadata =>
			values(pick(metadata, criteria.slice(0, criteria.length - 1))).join(),
		);

		// Group into a map of arrays that looks like this:
		// {
		//     '2018,MAP00001': PolicyDocumentMetadata[]
		//     '2017,MAP00001': PolicyDocumentMetadata[]
		// }
		const groupsObject = groupBy(
			metadatasArray,
			({ term, policyNumber }) => `${term},${policyNumber}`,
		);

		// Convert into PolicyPacket[].
		const packetsArray = map(
			groupsObject,
			value => new PolicyPacket(value[0].term, value[0].policyNumber, value),
		);

		// Sort each PolicyPacket by document name.
		for (const packet of packetsArray) {
			packet.metadatas.sort((a, b) => a.formName.localeCompare(b.formName));
		}

		return packetsArray;
	},

	packetizePolicyDocumentMetadatasForMmic(metadatasArray, policyNumber, effectiveDate) {
		// Deduplicate to accept only the newest revision of each document.
		const criteria = ['year', 'formId', 'riskName'];
		const currentYear = new Date().getFullYear();

		const effectiveYear = effectiveDate.getFullYear();
		metadatasArray = filter(metadatasArray, metadata => metadata.docType !== 'INVOICE');

		metadatasArray = sortBy(metadatasArray, criteria);
		metadatasArray = reverse(metadatasArray);

		const groupsObject = groupBy(metadatasArray, ({ year }) => `${year}`);

		// Convert into PolicyPacket[].
		let packetsArray = map(
			groupsObject,
			value => new PolicyPacket(value[0].year, policyNumber, value),
		);
		packetsArray.sort((a, b) => {
			a = parseInt(a.term, 10);
			b = parseInt(b.term, 10);
			return a > b ? -1 : a < b ? 1 : 0;
		});
		packetsArray = packetsArray.slice(0, 3);
		packetsArray.forEach(packet => {
			let riskName = '';
			const metadata = packet.metadatas.find(
				metadata =>
					metadata.docId !== 'EDS' &&
					metadata.docId !== 'PP' &&
					metadata.docId !== 'COI' &&
					(metadata.riskName !== undefined || metadata.policyHolder !== undefined),
			);
			riskName =
				metadata && metadata.riskName
					? metadata.riskName
					: metadata && metadata.policyHolder
					? metadata.policyHolder
					: '';

			packet.riskName = riskName;
			return packet;
		});

		packetsArray.sort((a: any, b: any) => {
			if (a.term < b.term) {
				return 1;
			}

			if (a.term > b.term) {
				return -1;
			}

			return 0;
		});

		return packetsArray;
	},
	filterBillingStatementsForMmic(metadatasArray) {
		metadatasArray = filter(metadatasArray, metadata => metadata.docType === 'INVOICE');

		const currentYear = new Date().getFullYear();
		metadatasArray = filter(
			metadatasArray,
			metadata =>
				metadata.year === currentYear ||
				metadata.year === currentYear - 1 ||
				metadata.year === currentYear - 2 ||
				metadata.year === currentYear - 3,
		);
		metadatasArray.sort((a, b) => {
			a = new Date(a.invoiceDate);
			b = new Date(b.invoiceDate);
			return a > b ? -1 : a < b ? 1 : 0;
		});
		return metadatasArray;
	},
};

export default PolicyDocuments;
